import { useRef, useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const API_BASE = process.env.REACT_APP_USER_SERVICE_URL;
const OTP_TTL = 5 * 60; // 5 minutes
const RESEND_COOLDOWN = 30; // seconds

export default function SellerMFA() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const email = state?.email;

  const [otp, setOtp] = useState(Array(6).fill(""));
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [expiresIn, setExpiresIn] = useState(OTP_TTL);
  const [resendCooldown, setResendCooldown] = useState(0);

  const inputsRef = useRef([]);

  /* -------- Guard -------- */
  useEffect(() => {
    if (!email) {
      navigate("/login", { replace: true });
    }
  }, [email, navigate]);

  /* -------- OTP Countdown -------- */
  useEffect(() => {
    if (expiresIn <= 0) return;

    const timer = setInterval(() => {
      setExpiresIn((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresIn]);

  /* -------- Resend Cooldown -------- */
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((p) => p - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const formatTime = (s) =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  /* -------- OTP Input -------- */
  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  /* -------- Paste Support -------- */
  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pasted.length !== 6) return;

    setOtp(pasted.split(""));
    inputsRef.current[5]?.focus();
  };

  const isComplete = otp.every(Boolean);
  const isExpired = expiresIn <= 0;

  /* -------- VERIFY OTP -------- */
  const handleVerify = async () => {
    if (!isComplete || isSubmitting || isExpired) return;

    try {
      setIsSubmitting(true);
      setError("");

      const response = await fetch(`${API_BASE}/sellers/mfa/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          otp: otp.join(""),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || "Invalid or expired code");
      }

      login({
        token: data.token,
        userId: data.userId,
        email: data.email,
        fullName: data.fullName,
        roles: data.roles,
        tokenType: data.tokenType,
        expiresIn: data.expiresIn,
      });

      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Verification failed");
    } finally {
      setIsSubmitting(false);
    }
  };
  /* -------- RESEND OTP -------- */
  const handleResend = async () => {
    if (isResending || resendCooldown > 0) return;

    try {
      setIsResending(true);
      setError("");

      await fetch(`${API_BASE}/sellers/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      // Reset state
      setOtp(Array(6).fill(""));
      setExpiresIn(OTP_TTL);
      setResendCooldown(RESEND_COOLDOWN);
      inputsRef.current[0]?.focus();
    } catch {
      setError("Failed to resend code. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="container-fluid min-vh-100">
      <div className="row min-vh-100">
        <div
          className="col-md-5 d-none d-md-flex align-items-center justify-content-center text-white"
          style={{ backgroundColor: "#041e42" }}
        >
          <h2 className="fw-semibold">Afrogebeya</h2>
        </div>

        <div className="col-12 col-md-7 d-flex flex-column">
          <div className="flex-grow-1 d-flex align-items-center justify-content-center px-3">
            <div className="card shadow-sm w-100" style={{ maxWidth: 420 }}>
              <div className="card-body p-4 text-center">
                <h3 className="fw-semibold mb-2">Verify your identity</h3>

                <p className="text-muted small mb-3">
                  Enter the 6-digit code sent to <strong>{email}</strong>
                </p>

                <div
                  className="d-flex justify-content-between mb-3"
                  onPaste={handlePaste}
                >
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputsRef.current[i] = el)}
                      className="form-control text-center fw-semibold"
                      style={{ width: 48, height: 56, fontSize: 20 }}
                      maxLength={1}
                      value={d}
                      onChange={(e) => handleChange(e.target.value, i)}
                      onKeyDown={(e) => handleKeyDown(e, i)}
                      disabled={isSubmitting || isExpired}
                    />
                  ))}
                </div>

                {error && (
                  <div className="alert alert-danger py-2 small">{error}</div>
                )}

                <button
                  className="btn btn-primary w-100 py-2 fw-semibold"
                  disabled={!isComplete || isSubmitting || isExpired}
                  onClick={handleVerify}
                >
                  {isSubmitting ? "Verifying…" : "VERIFY"}
                </button>

                <div className="mt-4 small text-muted">
                  {isExpired ? (
                    <>
                      OTP expired.{" "}
                      <button
                        className="btn btn-link p-0"
                        disabled={isResending || resendCooldown > 0}
                        onClick={handleResend}
                      >
                        <strong>
                          {resendCooldown > 0
                            ? `Resend in ${resendCooldown}s`
                            : "Resend code"}
                        </strong>
                      </button>
                    </>
                  ) : (
                    <>
                      OTP expires in{" "}
                      <strong className="text-danger">
                        {formatTime(expiresIn)}
                      </strong>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="text-center pb-3 small">
            <Link to="/terms">Terms and Conditions</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
